<template>
  <div class="metrics-chart">
    <div v-for="chart in charts" :key="chart.key" class="chart-card">
      <div class="chart-head">
        <span class="chart-title">{{ chart.title }}</span>
        <span class="chart-range">{{ chart.rangeText }}</span>
      </div>

      <e-chart :option="chart.option" height="200px" :group="CHART_GROUP" />

      <!-- 图例就是数值表：最新一轮每个指标是多少 -->
      <div class="legend">
        <div v-for="s in chart.series" :key="s.name" class="legend-item">
          <i class="dot" :style="{ background: s.color }"></i>
          <span class="legend-name">{{ s.label }}</span>
          <span class="legend-value">{{ format(s.last) }}</span>
        </div>
      </div>
    </div>

    <div v-if="!charts.length" class="chart-empty">暂无数据</div>

    <div v-else-if="truncated" class="chart-note">
      共 {{ rowsTotal }} 轮，图上只画最近 {{ rows.length }} 轮
    </div>
  </div>
</template>

<script>
import EChart from './EChart.vue';

// 同一位置在不同图里同色（如训练/验证损失）
const PALETTE = [
  '#409eff',
  '#67c23a',
  '#e6a23c',
  '#f56c6c',
  '#9b59b6',
  '#00b8a9',
  '#ff7f50',
  '#606266',
];

// 分组按列名前缀；带 (B)/(M) 的先各自成组，其余 metrics/ 归入默认组
const GROUP_DEFS = [
  { key: 'train', title: '训练损失', pick: name => name.startsWith('train/') },
  { key: 'val', title: '验证损失', pick: name => name.startsWith('val/') },
  {
    key: 'metrics-b',
    title: '检测指标（B）',
    pick: name => name.startsWith('metrics/') && name.endsWith('(B)'),
  },
  {
    key: 'metrics-m',
    title: '分割指标（M）',
    pick: name => name.startsWith('metrics/') && name.endsWith('(M)'),
  },
  { key: 'metrics', title: '指标', pick: name => name.startsWith('metrics/') },
  { key: 'lr', title: '学习率', pick: name => name.startsWith('lr/') },
];

// 这两列不画：epoch 是横轴，time 是累计秒数
const X_AXIS_COLUMNS = ['epoch', 'time'];

// 几张图的悬停竖线联动的组名
const CHART_GROUP = 'training-metrics';

/** `train/box_loss` → `box_loss`：前缀已在图标题里 */
function shortLabel(name) {
  const parts = name.split('/');
  return parts.length > 1 ? parts[parts.length - 1] : name;
}

/** 每轮指标曲线：纯展示，数据由详情抽屉传入；列名不认识时落「其它」一组照画 */
export default {
  name: 'TrainingMetricsChart',

  components: { EChart },

  props: {
    /** results.csv 表头，按列顺序 */
    columns: { type: Array, default: () => [] },
    /** 每一轮的取值，与 columns 下标对齐；读不出来的单元格是 null */
    rows: { type: Array, default: () => [] },
    /** 文件里的总轮数 */
    rowsTotal: { type: Number, default: 0 },
    truncated: { type: Boolean, default: false },
  },

  data() {
    return { CHART_GROUP };
  },

  computed: {
    /** 横轴：每一轮的轮次号（没有 epoch 列就按序号） */
    epochs() {
      const column = this.columns.indexOf('epoch');
      return this.rows.map((row, index) => {
        const value = column >= 0 ? row[column] : index;
        return typeof value === 'number' ? value : index;
      });
    },

    /** 每个分组一块图：系列、配色、量级、图例值、ECharts 配置一次备齐 */
    charts() {
      if (!this.columns.length || !this.rows.length) return [];
      const taken = new Set(X_AXIS_COLUMNS);
      const charts = [];
      // 默认组放最后：未被规则匹配的列也照画
      const defs = GROUP_DEFS.concat([{ key: 'other', title: '其它', pick: () => true }]);
      defs.forEach(def => {
        const series = [];
        this.columns.forEach((name, column) => {
          if (taken.has(name) || !def.pick(name)) return;
          taken.add(name);
          const values = this.rows.map(row => (typeof row[column] === 'number' ? row[column] : null));
          // 整列都没有值就不画
          if (!values.some(value => value !== null)) return;
          let last = null;
          for (let i = values.length - 1; i >= 0; i -= 1) {
            if (values[i] !== null) {
              last = values[i];
              break;
            }
          }
          series.push({
            name,
            label: shortLabel(name),
            values,
            color: PALETTE[series.length % PALETTE.length],
            last,
          });
        });
        if (series.length) charts.push(this.buildChart(def, series));
      });
      return charts;
    },
  },

  methods: {
    /** 一组指标拼成图：量级从这组的实际取值算，图例尾巴带上最新值 */
    buildChart(def, series) {
      let min = Infinity;
      let max = -Infinity;
      series.forEach(s => {
        s.values.forEach(value => {
          if (value === null) return;
          if (value < min) min = value;
          if (value > max) max = value;
        });
      });
      return {
        key: def.key,
        title: def.title,
        series,
        rangeText: `${this.format(min)} ~ ${this.format(max)}`,
        option: this.buildOption(series),
      };
    },

    buildOption(series) {
      return {
        // 监控每 10 秒重取一次，关动画避免重画闪烁
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line', lineStyle: { color: '#c0c4cc', type: 'dashed' } },
          backgroundColor: 'rgba(48, 49, 51, 0.92)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 11 },
          formatter: params => {
            const head = `第 ${params[0].axisValue} 轮`;
            const lines = params.map(item => `${item.marker}${item.seriesName} <b>${this.format(item.value)}</b>`);
            return [head, ...lines].join('<br/>');
          },
        },
        grid: { left: 8, right: 16, top: 10, bottom: 4, containLabel: true },
        xAxis: {
          type: 'category',
          data: this.epochs,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: {
            color: '#a8abb2',
            fontSize: 10,
            hideOverlap: true,
            showMinLabel: true,
            showMaxLabel: true,
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLabel: { color: '#a8abb2', fontSize: 10, formatter: value => this.format(value) },
          splitLine: { lineStyle: { color: '#f0f2f5' } },
        },
        series: series.map(s => ({
          name: s.label,
          type: 'line',
          data: s.values,
          showSymbol: false,
          symbolSize: 5,
          lineStyle: { width: 1.6, color: s.color },
          itemStyle: { color: s.color },
          emphasis: { focus: 'series' },
        })),
      };
    },

    /** 数值按量级换写法（loss 1.x、mAP 0.x、学习率 1e-5）；没有值给破折号，不补 0 */
    format(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) return '-';
      const abs = Math.abs(value);
      if (abs !== 0 && abs < 0.001) return value.toExponential(1);
      if (abs < 1) return value.toFixed(4);
      return value.toFixed(3);
    },
  },
};
</script>

<style scoped>
.chart-card {
  margin-bottom: 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 10px 4px;
}

.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}

.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.chart-range {
  font-size: 11px;
  color: #909399;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  padding: 2px 0 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-right: 14px;
  font-size: 11px;
  line-height: 18px;
}

.legend-name {
  color: #606266;
  margin-right: 4px;
}

.legend-value {
  color: #303133;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 4px;
  flex: none;
}

.chart-note {
  font-size: 11px;
  color: #909399;
}

.chart-empty {
  padding: 12px 0;
  font-size: 12px;
  color: #909399;
}
</style>
